import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { PageActions, PasslyStepper } from '@app/components';
import { hasOrgPermission, genericErrorString } from '@app/helpers';
import DownloadMigratorComponent from '@app/pages/password/components/downloadMigrator.component';
import UploadMigratorFileComponent from '@app/pages/password/components/uploadMigratorFile.component';
import SecretKeyForMigratorComponent from '@app/pages/password/components/secretKeyForMigrator.component';
import InitialGroupForMigratorComponent from '@app/pages/password/components/initialGroupForMigrator.component';
import InitialPasswordPolicyMigrator from '@app/pages/password/components/initialPasswordPolicyMigrator.component';
import DoMigrationComponent from '@app/pages/password/components/DoMigration.component';
import { IListPageBarEnabled } from '@app/models';
import { ManageVault, ManageSystem } from '@app/store/constants';
import { vaultPasswordsService } from '@app/services';
import { useTypedTranslation } from '@app/hooks';
import { handleSortingMenu, addDetailsBreadcrumb } from '@app/store/actions';

const MigrateOnPremPage = () => {
    const { translation } = useTypedTranslation();
    const dispatch = useDispatch();

    const [orgAllowedToViewThisPage, setOrgAllowedToViewThisPage] = useState(false);
    const [hasPermission, setHasPermission] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [uploadedFile, setUploadedFile] = useState([]);
    const [secretKey, setSecretKey] = useState('');
    const [initialGroup, setInitialGroup] = useState('');
    const [initialPolicyId, setInitialPolicyId] = useState('');

    const [errorStep, setErrorStep] = useState();
    const steps = getSteps();

    const organizationFilter = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: false,
        organizationsEnabled: true,
        actionMenuEnabled: false
    };

    useEffect(() => {
        dispatch(handleSortingMenu(setListPageBarEnabledOptions));

        setTimeout(function () {
            dispatch(addDetailsBreadcrumb(translation('ON_PREM_MIGRATOR_TITLE')));
        }, 300);

    }, []);

    useEffect(() => {
        if (activeStep === steps.length) {
            let errors: string = '';

            // this is the last step so make sure all form fields are filled in.
            if (uploadedFile.length === 0) {
                // error
                errors += translation('UPLOAD') + ' ' + translation('IS_REQUIRED') + '. ';
                setActiveStep(1);
            }
            if (secretKey === '') {
                // error
                if (errors === '') {
                    setActiveStep(2);
                }

                errors += translation('ON_PREM_MIGRATOR_ENCRYPTION_WORD') + ' ' + translation('IS_REQUIRED');
            } else if (initialPolicyId === '') {
                if (errors === '') {
                    setActiveStep(3);
                }

                errors += translation('SYNC_PASSWORD_POLICY') + ' ' + translation('IS_REQUIRED');
            }

            setErrorMessage(errors);
        }
    }, [activeStep]);

    useEffect(() => {
        if (organizationFilter !== undefined) {
            setup();
        }
    }, [organizationFilter]);

    const setup = () => {
        setHasPermission(hasOrgPermission([ManageVault, ManageSystem]));
        vaultPasswordsService.orgCanMigrateAjax(handleErrorOrgCanMigrate, handleSuccessOrgCanMigrate);
        setInitialGroup('');
    };

    const handleSuccessOrgCanMigrate = () => {
        // if it didn't throw an error then it is true
        setOrgAllowedToViewThisPage(true);
    };

    const handleErrorOrgCanMigrate = (error: any) => {
        if (error.response.status === 400 && error.response.data.error.message === 'ORG_CANNOT_VIEW') {
            setOrgAllowedToViewThisPage(false);
        }
        else {
            handleError(error);
        }
    };

    const handleError = (errorResponse: any) => {
        setErrorMessage(genericErrorString(errorResponse));
    };

    function getSteps() {
        return [
            translation('ON_PREM_MIGRATOR_DOWNLOAD'),
            translation('UPLOAD'),
            translation('ON_PREM_MIGRATOR_ENCRYPTION_WORD'),
            translation('SYNC_PASSWORD_POLICY'),
            translation('INITIAL_GROUP')];
    }

    function getStepContent(step: number) {
        switch (step) {
            case 0:
                return <DownloadMigratorComponent />;
            case 1:
                return <UploadMigratorFileComponent returnFile={getFileUploaded} />;
            case 2:
                return <SecretKeyForMigratorComponent returnKey={getSecretKey} currentKey={secretKey} />;
            case 3:
                return <InitialPasswordPolicyMigrator returnPolicy={getInitialPolicy} />;
            case 4:
                return <InitialGroupForMigratorComponent returnGroup={getInitialGroup} />;
            default:
                return 'Unknown step';
        }
    }

    const getFileUploaded = (file: any) => {
        setUploadedFile(file);
        setErrorMessage('');
    };

    const getSecretKey = (key: string) => {
        setSecretKey(key);
        setErrorMessage('');
    };

    const getInitialGroup = (group: string) => {
        setInitialGroup(group);
    };

    const getInitialPolicy = (policyId: string) => {
        setInitialPolicyId(policyId);
    };

    const handleNext = () => {
        setActiveStep(prevActiveStep => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1);
    };

    return (
        <>
        {hasPermission && <PasslyStepper steps={steps} activeStep={activeStep} errorStep={errorStep} />}
            <div className ='migrateOnPrem'>
                {orgAllowedToViewThisPage ? <>
                    {!hasPermission ?
                        <div className='migrateOnPrem__no-perm'>{translation('VAULT_MIGRATE_NO_PERM')}</div>
                        :
                        <>
                            <div className='migrateOnPrem__has-perm'>
                                {activeStep === steps.length ? (
                                    <DoMigrationComponent file={uploadedFile} secretKey={secretKey} initialGroup={initialGroup} initialPolicy={initialPolicyId}/>
                                ) : (
                                        <div className='migrateOnPrem__has-perm-details'>
                                            <div className='migrateOnPrem__has-perm-details-uploader'>{getStepContent(activeStep)}</div>
                                            <PageActions
                                                primaryBtn={activeStep === steps.length - 1 ? translation('ON_PREM_MIGRATOR_MIGRATE_PWD') : translation('RESET_NEXT')}
                                                secondaryBtn={translation('BACK')}
                                                onClickPrimary={handleNext}
                                                onClickSecondary={activeStep !== 0 ? handleBack : undefined}
                                            />
                                        </div>
                                    )}
                            </div>
                        </>
                    }
                </>
                    :
                    <div className='migrateOnPrem__no-view'>{translation('ORG_CANNOT_VIEW')}</div>
                }

                {
                    errorMessage &&
                    <div className='modal__error'>{errorMessage}</div>
                }
            </div>
        </>
    );

};

export default MigrateOnPremPage;